import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { WhatsAppIcon } from "@/components/common";
import { ExternalLink, Mail } from "lucide-react";

/* WhatsApp send dialog: approved templates only, preview, provider status,
   wa.me deep-link fallback when Interakt is off. */
export const WhatsAppDialog = ({ contact, opportunityId, open, setOpen, onSent }) => {
  const [data, setData] = useState(null);
  const [kind, setKind] = useState("booking_invitation");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open) api.get("/comms/whatsapp/templates").then((r) => setData(r.data));
  }, [open]);

  const tpl = data?.templates?.[kind];
  const send = async () => {
    setSending(true);
    try {
      const { data: res } = await api.post("/comms/whatsapp/send", {
        contact_id: contact.contact_id, kind, opportunity_id: opportunityId,
      });
      if (res.status === "queued") {
        toast.success("WhatsApp queued — Interakt will deliver it");
      } else {
        toast.warning("Provider off — logged as Pending provider. Use the deep link to send manually.");
        if (res.deep_link) window.open(res.deep_link, "_blank");
      }
      onSent?.();
      setOpen(false);
    } catch (e) { toast.error(errDetail(e)); } finally { setSending(false); }
  };

  const openChat = async () => {
    try {
      const { data: d } = await api.get(`/comms/whatsapp/deeplink/${contact.contact_id}`);
      window.open(d.deep_link, "_blank");
    } catch (e) { toast.error(errDetail(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <WhatsAppIcon className="h-4 w-4 text-[#25D366]" /> WhatsApp — {contact?.full_name}
            {data && (
              <Badge variant="outline" className="text-[10px]">
                {data.provider_enabled ? "Interakt LIVE" : "Provider off — manual fallback"}
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Approved template</Label>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger data-testid="wa-kind-select" className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(data?.templates || {}).map(([k, t]) => (
                  <SelectItem key={k} value={k}>{t.purpose || k}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {tpl && (
            <div className="rounded-md bg-muted/60 p-3 text-xs space-y-1">
              <p><span className="text-muted-foreground">Template:</span> <span className="font-mono">{tpl.name || "not mapped yet"}</span> ({tpl.language || "en"})</p>
              <p><span className="text-muted-foreground">Preview variables:</span> {contact?.full_name?.split(" ")[0]}</p>
              <p className="text-muted-foreground">Only admin-approved templates can be sent — free-form AI messages are not permitted.</p>
            </div>
          )}
          <Button data-testid="wa-open-chat" variant="outline" size="sm" className="w-full" onClick={openChat}>
            <ExternalLink className="h-3.5 w-3.5 mr-1.5" /> Open WhatsApp chat (wa.me)
          </Button>
        </div>
        <DialogFooter>
          <Button data-testid="wa-send-button" disabled={sending} onClick={send}>
            {sending ? "Queuing…" : "Send template"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* Quick WhatsApp: no template picker — this always opens WhatsApp Web/App
   via a deep link, so a template selection has nothing to act on (Interakt's
   real API send lives in WhatsAppDialog above, for the formal, compliance-
   bound notifications). Just an editable prefilled message. */
export const QuickWhatsAppDialog = ({ contact, presetMessage, open, setOpen }) => {
  const [message, setMessage] = useState("");
  useEffect(() => {
    if (!open) return;
    const firstName = contact?.full_name?.split(" ")[0] || "";
    setMessage(presetMessage || `Hi ${firstName}, this is Aussie Pathways calling regarding your enquiry.`);
  }, [open, presetMessage, contact]);

  const openChat = async () => {
    try {
      const { data } = await api.get(`/comms/whatsapp/deeplink/${contact.contact_id}`, { params: { text: message } });
      window.open(data.deep_link, "_blank");
      setOpen(false);
    } catch (e) { toast.error(errDetail(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <WhatsAppIcon className="h-4 w-4 text-[#25D366]" /> WhatsApp — {contact?.full_name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label className="text-xs">Message (edit freely — opens in WhatsApp Web/App, not sent from here)</Label>
          <Textarea data-testid="quick-wa-message" rows={4} value={message} onChange={(e) => setMessage(e.target.value)} />
        </div>
        <DialogFooter>
          <Button data-testid="quick-wa-open" onClick={openChat} disabled={!contact?.contact_id}>
            <WhatsAppIcon className="h-4 w-4 mr-1.5" /> Open WhatsApp
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* Email compose dialog: templates + merge fields -> Gmail compose deep link. */
export const EmailDialog = ({ contact, opportunityId, open, setOpen }) => {
  const [templates, setTemplates] = useState({});
  const [key, setKey] = useState("intro");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");

  useEffect(() => {
    if (open) api.get("/comms/email/templates").then((r) => {
      setTemplates(r.data || {});
      const first = Object.keys(r.data || {})[0];
      if (first) setKey(first);
    });
  }, [open]);

  const compose = async () => {
    try {
      const { data } = await api.post("/comms/email/compose", {
        contact_id: contact.contact_id, template_key: subject ? null : key,
        subject: subject || null, body: body || null, opportunity_id: opportunityId,
      });
      window.open(data.gmail_url, "_blank");
      toast.success("Gmail compose opened — communication logged");
      setOpen(false);
    } catch (e) { toast.error(errDetail(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Mail className="h-4 w-4" /> Email — {contact?.full_name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Template</Label>
            <Select value={key} onValueChange={setKey}>
              <SelectTrigger data-testid="email-template-select" className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(templates).map(([k, t]) => (
                  <SelectItem key={k} value={k}>{t.subject?.slice(0, 50) || k}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Custom subject (optional — overrides template)</Label>
            <Input data-testid="email-subject" value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>
          {subject && (
            <Textarea data-testid="email-body" rows={4} placeholder="Custom body…"
              value={body} onChange={(e) => setBody(e.target.value)} />
          )}
          <p className="text-[11px] text-muted-foreground">Merge fields {"{name} {first_name} {customer_id}"} are filled automatically. Opens Gmail compose; the interaction is logged in SalesOS.</p>
        </div>
        <DialogFooter>
          <Button data-testid="email-compose-button" onClick={compose}>Open in Gmail</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
